/**
 * useSymptomLanguage Hook
 * 
 * Manages language selection, STT configuration, and translation for the Symptom Checker.
 * 
 * Features:
 * - Single language selection (user selects their preferred language)
 * - STT provider selection based on language (Google for Swahili, Deepgram for others)
 * - Automatic translation of user speech to English for DDX transcript
 * - Translation of system questions to user's language
 * 
 * Language Flow:
 * 1. User speaks in selected language (e.g., Spanish)
 * 2. STT recognizes speech in that language
 * 3. Speech is translated to English for the DDX transcript
 * 4. System generates questions (in English internally)
 * 5. Questions are translated to user's language before TTS
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { 
  SUPPORTED_LANGUAGES, 
  getLanguageByCode, 
  requiresGoogleSTT,
  shouldUseFlux as shouldUseFluxForLang,
  getDeepgramModel as getDeepgramModelForLang,
  getDeepgramLanguageCode,
} from './constants';

// Import translation service from ChartMind
// This uses the same Firebase-backed translation as ChartMind
import { translateText } from '../../../../chartmind/components/chartmind/services/translationService';

// STT Provider types — canonical definition lives in translationSTTManager
import { STT_PROVIDER } from '../../../../../services/voice/translationSTTManager';
export { STT_PROVIDER };

/**
 * useSymptomLanguage Hook
 * 
 * @param {Object} options - Configuration options
 * @param {string} options.defaultLanguage - Default language code (defaults to 'en-US')
 * @param {Function} options.onLanguageChange - Callback when language changes
 */
export function useSymptomLanguage(options = {}) {
  const {
    defaultLanguage = 'en-US',
    onLanguageChange,
  } = options;
  
  // Language state
  const [language, setLanguageState] = useState(defaultLanguage);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState(null);
  
  // Sync internal state when parent passes a new defaultLanguage (controlled mode)
  useEffect(() => {
    setLanguageState(defaultLanguage);
  }, [defaultLanguage]);

  // Refs for callbacks to avoid stale closures
  const onLanguageChangeRef = useRef(onLanguageChange);
  onLanguageChangeRef.current = onLanguageChange;
  
  // Cache for translations to avoid redundant API calls
  const translationCacheRef = useRef(new Map());
  
  /**
   * Get language info for the selected language
   */
  const languageInfo = getLanguageByCode(language);
  
  /**
   * Check if translation is needed (not English)
   */
  const needsTranslation = language !== 'en-US';
  
  /**
   * Get the appropriate STT provider for the current language
   */
  const getSTTProvider = useCallback(() => {
    if (requiresGoogleSTT(language)) {
      return STT_PROVIDER.GOOGLE;
    }
    return STT_PROVIDER.DEEPGRAM;
  }, [language]);
  
  /**
   * Check if Deepgram Flux should be used for the current language
   * Flux provides model-integrated end-of-turn detection (currently English only)
   */
  const shouldUseFlux = useCallback(() => {
    return shouldUseFluxForLang(language);
  }, [language]);
  
  /**
   * Get the Deepgram model to use for the current language
   * Returns 'flux' for English, 'nova-3' for other Deepgram languages, null for non-Deepgram
   */
  const getDeepgramModel = useCallback(() => {
    return getDeepgramModelForLang(language);
  }, [language]);
  
  /**
   * Get STT configuration for the current language
   * Returns provider-specific config
   */
  const getSTTConfig = useCallback(() => {
    const provider = getSTTProvider();
    
    if (provider === STT_PROVIDER.GOOGLE) {
      // Google Cloud STT configuration
      return {
        provider: STT_PROVIDER.GOOGLE,
        languageCode: language,
        useFlux: false,
        // Google-specific options
        enableAutomaticPunctuation: true,
        model: 'latest_short', // Best for conversational speech
      };
    }
    
    // Deepgram configuration
    const deepgramLang = getDeepgramLanguageCode(language);
    const useFlux = shouldUseFluxForLang(language);
    
    if (useFlux) {
      // Flux configuration (English only)
      return {
        provider: STT_PROVIDER.DEEPGRAM,
        language: deepgramLang,
        model: 'flux',
        useFlux: true,
        // Flux-specific options for end-of-turn detection
        eotThreshold: 0.7,        // End-of-turn confidence threshold (0.5-0.9)
        eagerEotThreshold: 0.5,   // Eager end-of-turn for speculative processing
        eotTimeoutMs: 5000,       // Max wait time for end-of-turn
      };
    }
    
    // Nova-3 configuration (non-English Deepgram languages)
    return {
      provider: STT_PROVIDER.DEEPGRAM,
      language: deepgramLang,
      model: 'nova-3',
      useFlux: false,
      // Nova-3 specific options
      smartFormat: true,
      punctuate: true,
    };
  }, [language, getSTTProvider]);
  
  /**
   * Get Web Speech API language code
   * Used for browser-native speech recognition
   */
  const getWebSpeechLanguage = useCallback(() => {
    // Map our language codes to BCP-47 codes for Web Speech API
    const webSpeechMap = {
      'en-US': 'en-US',
      'es-ES': 'es-ES',
      'fr-FR': 'fr-FR',
      'sw-KE': 'sw-KE', // Note: Browser support for Swahili may vary
    };
    return webSpeechMap[language] || 'en-US';
  }, [language]);
  
  /**
   * Set the language and notify callbacks
   */
  const setLanguage = useCallback((newLanguage) => {
    const langInfo = getLanguageByCode(newLanguage);
    if (!langInfo) {
      console.warn(`[useSymptomLanguage] Unknown language: ${newLanguage}`);
      return;
    }
    
    setLanguageState(newLanguage);
    
    // Notify callback
    onLanguageChangeRef.current?.(newLanguage, langInfo);
  }, []);
  
  /**
   * Core translate-with-cache helper. Both translateToEnglish and translateQuestion
   * delegate here to avoid duplicating the cache-check / translate / store / evict / error pattern.
   *
   * @param {string} text     - Text to translate
   * @param {string} fromLang - Source language code
   * @param {string} toLang   - Target language code
   * @returns {Promise<string>} Translated text (falls back to original on error)
   */
  const cachedTranslate = useCallback(async (text, fromLang, toLang) => {
    // Skip if same language or empty text
    if (fromLang === toLang || !text || text.trim().length === 0) {
      return text;
    }

    // Check cache first
    const cacheKey = `${fromLang}→${toLang}:${text}`;
    if (translationCacheRef.current.has(cacheKey)) {
      return translationCacheRef.current.get(cacheKey);
    }

    setIsTranslating(true);
    setTranslationError(null);

    try {
      const result = await translateText(text, fromLang, toLang);

      // Cache the result (FIFO eviction at 100 entries)
      translationCacheRef.current.set(cacheKey, result.translation);
      if (translationCacheRef.current.size > 100) {
        const firstKey = translationCacheRef.current.keys().next().value;
        translationCacheRef.current.delete(firstKey);
      }

      return result.translation;
    } catch (error) {
      console.error(`[useSymptomLanguage] Translation failed (${fromLang}→${toLang}):`, error);
      setTranslationError(error);
      // Return original text on error (better than nothing)
      return text;
    } finally {
      setIsTranslating(false);
    }
  }, []);

  /**
   * Translate user speech to English for the DDX transcript
   */
  const translateToEnglish = useCallback(
    (text) => cachedTranslate(text, language, 'en-US'),
    [language, cachedTranslate]
  );

  /**
   * Translate a question from English to the user's language
   */
  const translateQuestion = useCallback(
    (question) => cachedTranslate(question, 'en-US', language),
    [language, cachedTranslate]
  );
  
  /**
   * Clear the translation cache
   */
  const clearTranslationCache = useCallback(() => {
    translationCacheRef.current.clear();
  }, []);
  
  return {
    // State
    language,
    languageInfo,
    needsTranslation,
    isTranslating,
    translationError,
    
    // Actions
    setLanguage,
    translateToEnglish,
    translateQuestion,
    clearTranslationCache,
    
    // STT Configuration
    getSTTProvider,
    getSTTConfig,
    getWebSpeechLanguage,
    
    // Deepgram Flux
    shouldUseFlux,
    getDeepgramModel,
    
    // Constants
    SUPPORTED_LANGUAGES,
    STT_PROVIDER,
  };
}

export default useSymptomLanguage;
